import React, { useState } from "react";
import { MoreHorizontal, Trash2, Undo2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useHasPermission } from "../contexts/PermissionsContext";
import { toast } from "sonner";

/**
 * Reusable "row actions" dropdown for soft-delete / hard-delete / restore.
 *
 * Props:
 *  - entity: catalog group name (e.g. "employees", "tasks") — used for permission keys
 *  - endpoint: REST base, e.g. "/employees" or "/leave/requests"
 *  - id: row id
 *  - label: human-readable identifier (e.g. employee_code or task title) — used in hard-delete confirmation
 *  - deleted: boolean — whether this row is currently soft-deleted
 *  - moduleId: optional module id for module-scoped permission checks
 *  - hardDeleteSupported: default true; set false to hide "Delete permanently" (e.g., wage_rates/discipline/leave)
 *  - extraSlot: optional ReactNode rendered above the delete items (e.g. Edit / View)
 *  - onChange: callback after a successful mutation (parent should reload)
 */
export default function RowActions({
    entity,
    endpoint,
    id,
    label,
    deleted = false,
    moduleId = null,
    hardDeleteSupported = true,
    extraSlot = null,
    onChange,
    testid,
    disabledHardReason = null,
}) {
    const canDelete = useHasPermission(`${entity}.delete`, moduleId);
    const canHardDelete = useHasPermission(`${entity}.hard_delete`, moduleId);
    const canWrite = useHasPermission(`${entity}.write`, moduleId);

    const [softOpen, setSoftOpen] = useState(false);
    const [hardOpen, setHardOpen] = useState(false);
    const [restoreOpen, setRestoreOpen] = useState(false);
    const [hardConfirm, setHardConfirm] = useState("");
    const [softReason, setSoftReason] = useState("");
    const [busy, setBusy] = useState(false);

    const showSoft = !deleted && canDelete;
    const showHard = deleted && canHardDelete && hardDeleteSupported;
    const showRestore = deleted && canWrite;

    const anyAction = showSoft || showHard || showRestore;
    if (!anyAction && !extraSlot) return null;

    const doSoft = async () => {
        setBusy(true);
        try {
            await api.delete(`${endpoint}/${id}`, { data: softReason ? { reason: softReason } : undefined });
            toast.success(`Deleted${label ? ` — ${label}` : ""}`);
            setSoftOpen(false); setSoftReason("");
            onChange && onChange();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const doHard = async () => {
        setBusy(true);
        try {
            const { data } = await api.delete(`${endpoint}/${id}/hard`);
            const cascaded = data?.cascaded;
            const cascadeMsg = cascaded
                ? ` (cascaded: ${Object.entries(cascaded).filter(([_, v]) => v > 0).map(([k, v]) => `${k}=${v}`).join(", ") || "none"})`
                : "";
            toast.success(`Permanently deleted${cascadeMsg}`);
            setHardOpen(false); setHardConfirm("");
            onChange && onChange();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const doRestore = async () => {
        setBusy(true);
        try {
            await api.post(`${endpoint}/${id}/restore`);
            toast.success(`Restored${label ? ` — ${label}` : ""}`);
            setRestoreOpen(false);
            onChange && onChange();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={testid || `row-actions-${id}`}>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    {extraSlot}
                    {extraSlot && anyAction && <DropdownMenuSeparator />}
                    {anyAction && <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Danger zone</DropdownMenuLabel>}
                    {showSoft && (
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSoftOpen(true); }} data-testid={`row-soft-delete-${id}`}>
                            <Trash2 className="h-4 w-4 mr-2 text-rose-600" /> Delete
                        </DropdownMenuItem>
                    )}
                    {showHard && (
                        <DropdownMenuItem
                            disabled={!!disabledHardReason}
                            title={disabledHardReason || ""}
                            onSelect={(e) => { e.preventDefault(); if (!disabledHardReason) setHardOpen(true); }}
                            data-testid={`row-hard-delete-${id}`}
                        >
                            <AlertTriangle className="h-4 w-4 mr-2 text-rose-700" /> Delete permanently
                        </DropdownMenuItem>
                    )}
                    {showRestore && (
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setRestoreOpen(true); }} data-testid={`row-restore-${id}`}>
                            <Undo2 className="h-4 w-4 mr-2 text-emerald-700" /> Restore
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Soft delete confirmation */}
            <Dialog open={softOpen} onOpenChange={setSoftOpen}>
                <DialogContent data-testid="soft-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete {label || "this record"}?</DialogTitle>
                        <DialogDescription>This is a soft delete — the record is hidden from default views and can be restored later.</DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-[color:var(--asl-muted)]">Reason (optional)</label>
                        <Textarea value={softReason} onChange={(e) => setSoftReason(e.target.value)} placeholder="Why are you deleting?" data-testid="soft-delete-reason" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSoftOpen(false)} data-testid="soft-delete-cancel">Cancel</Button>
                        <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doSoft} disabled={busy} data-testid="soft-delete-confirm">
                            {busy ? "Deleting…" : "Delete"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Hard delete confirmation */}
            <Dialog open={hardOpen} onOpenChange={setHardOpen}>
                <DialogContent data-testid="hard-delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-rose-700">Permanently delete {label || "this record"}?</DialogTitle>
                        <DialogDescription>
                            This action <strong>cannot be undone</strong>. Related rows (where applicable) will be cascaded.
                            Type <span className="mono font-semibold">{label || id}</span> to confirm.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        value={hardConfirm}
                        onChange={(e) => setHardConfirm(e.target.value)}
                        placeholder={label || id}
                        data-testid="hard-delete-confirm-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setHardOpen(false)} data-testid="hard-delete-cancel">Cancel</Button>
                        <Button
                            className="bg-rose-700 hover:bg-rose-800 text-white"
                            onClick={doHard}
                            disabled={busy || hardConfirm.trim() !== String(label || id).trim()}
                            data-testid="hard-delete-confirm"
                        >
                            {busy ? "Erasing…" : "Yes, delete permanently"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Restore confirmation */}
            <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
                <DialogContent data-testid="restore-dialog">
                    <DialogHeader>
                        <DialogTitle>Restore {label || "this record"}?</DialogTitle>
                        <DialogDescription>The record will be brought back into the active list.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRestoreOpen(false)} data-testid="restore-cancel">Cancel</Button>
                        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doRestore} disabled={busy} data-testid="restore-confirm">
                            {busy ? "Restoring…" : "Restore"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
